import { useEffect, useState, useRef } from 'react';
import AdvancedCanvas from './AdvancedCanvas';
import AdvancedToolbar from './AdvancedToolbar';
import AdvancedControls from './AdvancedControls';
import useAdvancedEditorStore from '../../store/useAdvancedEditorStore';
import backendService from '../../services/backendService';
import { loadImageFromUrl, resizeImage } from '../../utils/imageProcessing';

/**
 * Modal de Edição Avançada
 * Integrado com useAdvancedEditorStore e backend completo
 */
const AdvancedEditorModal = ({
  isOpen,
  onClose,
  image,           // URL da imagem original
  initialMask,     // ImageData da máscara inicial (opcional)
  onApply
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const hasInitialized = useRef(false);

  const {
    originalImage,
    mask,
    currentModel,
    processingSettings,
    isProcessing,
    setOriginalImage,
    setOriginalFile,
    setMask,
    setCurrentModel,
    setIsProcessing,
    reset,
  } = useAdvancedEditorStore();

  /**
   * Inicializa o editor quando o modal abre
   */
  useEffect(() => {
    if (isOpen && image && !hasInitialized.current) {
      initializeEditor();
      hasInitialized.current = true;
    }

    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen, image]);

  /**
   * Inicializa o editor com a imagem
   */
  const initializeEditor = async () => {
    setIsLoading(true);
    setLoadingMessage('Carregando imagem...');

    try {
      console.log('🖼️ Modal carregando imagem de:', image?.substring(0, 50) + '...');

      // Carrega a imagem ORIGINAL (antes de qualquer processamento)
      const img = await loadImageFromUrl(image);

      // Redimensiona para max 1280px (mantém original se menor)
      setLoadingMessage('Preparando canvas...');
      const canvas = resizeImage(img, 1280);

      setOriginalImage(canvas);

      // Se tem máscara inicial, usa ela
      if (initialMask) {
        setMask(initialMask, true);
      } else {
        // Cria máscara vazia (tudo branco - tudo visível)
        const ctx = canvas.getContext('2d');
        const emptyMask = ctx.createImageData(canvas.width, canvas.height);

        // Preenche com branco (255, 255, 255, 255)
        for (let i = 0; i < emptyMask.data.length; i += 4) {
          emptyMask.data[i] = 255;
          emptyMask.data[i + 1] = 255;
          emptyMask.data[i + 2] = 255;
          emptyMask.data[i + 3] = 255;
        }

        setMask(emptyMask, true);
      }

      // Converte canvas para blob para reprocessamento
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      });

      const file = new File([blob], 'image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      setOriginalFile(file);
    } catch (error) {
      console.error('Erro ao inicializar editor:', error);
      alert('Erro ao carregar imagem no editor avançado');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handler para trocar de modelo
   */
  const handleModelChange = async (newModel) => {
    if (newModel === currentModel || !originalImage) return;

    setIsProcessing(true);
    setIsLoading(true);
    setLoadingMessage(`Processando com modelo ${newModel}...`);

    try {
      // Pega o arquivo original
      const canvas = originalImage;
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      });

      const file = new File([blob], 'image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Remove background com novo modelo
      const result = await backendService.removeBackground(file, {
        model: newModel,
        alphaMatting: processingSettings.alphaMatting,
        alphaMattingForegroundThreshold: processingSettings.alphaMattingForegroundThreshold,
        alphaMattingBackgroundThreshold: processingSettings.alphaMattingBackgroundThreshold,
        alphaMattingErodeSize: processingSettings.alphaMattingErodeSize,
      });

      // Converte blob para máscara
      const generatedMask = await backendService.blobToMask(
        result.blob,
        canvas.width,
        canvas.height
      );

      setMask(generatedMask, true);
      setCurrentModel(newModel);

      console.log(`✅ Modelo alterado para ${newModel} em ${result.duration}s`);
    } catch (error) {
      console.error('Erro ao trocar modelo:', error);
      alert('Erro ao processar com novo modelo. Tente novamente.');
    } finally {
      setIsProcessing(false);
      setIsLoading(false);
    }
  };

  /**
   * Handler para reprocessar com configurações atuais
   */
  const handleReprocess = async () => {
    if (!originalImage) return;

    setIsProcessing(true);
    setIsLoading(true);
    setLoadingMessage('Reprocessando...');

    try {
      const canvas = originalImage;
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      });

      const file = new File([blob], 'image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      const result = await backendService.removeBackground(file, {
        model: currentModel,
        alphaMatting: processingSettings.alphaMatting,
        alphaMattingForegroundThreshold: processingSettings.alphaMattingForegroundThreshold,
        alphaMattingBackgroundThreshold: processingSettings.alphaMattingBackgroundThreshold,
        alphaMattingErodeSize: processingSettings.alphaMattingErodeSize,
      });

      const generatedMask = await backendService.blobToMask(
        result.blob,
        canvas.width,
        canvas.height
      );

      setMask(generatedMask, true);
      console.log(`✅ Reprocessado em ${result.duration}s`);
    } catch (error) {
      console.error('Erro ao reprocessar:', error);
      alert('Erro ao reprocessar imagem. Tente novamente.');
    } finally {
      setIsProcessing(false);
      setIsLoading(false);
    }
  };

  /**
   * Handler para aplicar mudanças
   */
  const handleApply = () => {
    if (!originalImage || !mask) return;

    // Aplica máscara na imagem
    const canvas = document.createElement('canvas');
    canvas.width = originalImage.width;
    canvas.height = originalImage.height;
    const ctx = canvas.getContext('2d');

    // Desenha imagem original
    ctx.drawImage(originalImage, 0, 0);

    // Pega dados da imagem
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const maskData = mask.data;

    // Aplica máscara no canal alpha
    for (let i = 0; i < data.length; i += 4) {
      data[i + 3] = maskData[i]; // Usa o valor R da máscara como alpha
    }

    ctx.putImageData(imageData, 0, 0);

    // Converte para data URL
    const resultUrl = canvas.toDataURL('image/png');

    // Callback para o pai
    if (onApply) {
      onApply(resultUrl, mask);
    }

    handleClose();
  };

  /**
   * Handler para fechar modal
   */
  const handleClose = () => {
    reset();
    onClose();
  };

  /**
   * Handler para atalhos de teclado
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // ESC para fechar
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full h-full flex flex-col bg-gray-50">
        {/* Toolbar */}
        <AdvancedToolbar
          currentModel={currentModel}
          onModelChange={handleModelChange}
          isProcessing={isLoading || isProcessing}
          onClose={handleClose}
          onApply={handleApply}
        />

        {/* Conteúdo principal */}
        <div className="flex-1 flex overflow-hidden">
          {/* Canvas */}
          <div className="flex-1 overflow-hidden">
            <AdvancedCanvas />
          </div>

          {/* Painel de controles */}
          <AdvancedControls onReprocess={handleReprocess} />
        </div>

        {/* Loading overlay */}
        {(isLoading || isProcessing) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-lg font-semibold text-gray-900">
                  {loadingMessage || 'Processando...'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedEditorModal;
